import React from "react";
import { GraduationCap, Code2, TrendingUp, Sparkles, Dumbbell, BookOpen, Car } from "lucide-react";

const stats = [
  { label: "Years in AU Wealth", value: "5+", sub: "Exclusive Australian market experience" },
  { label: "Practice FUM Worked With", value: "$200M+", sub: "Top practices, each $200M+ FUM" },
  { label: "Complex SOAs Delivered", value: "400+", sub: "Across SMSF, gearing, trusts" },
  { label: "ROAs & Reviews Delivered", value: "700+", sub: "Ongoing review documents" },
];

const techPoints = [
  {
    icon: GraduationCap,
    title: "CFA Level 2 Candidate",
    body: "Chartered Financial Analyst pathway. Deep technical investment and portfolio theory grounding.",
  },
  {
    icon: Code2,
    title: "B-Tech, Computer Science",
    body: "Fluent across multiple AI models, Excel VBA, Power Automate and no code stacks. Automation built into every workflow.",
  },
  {
    icon: TrendingUp,
    title: "Software Mastery",
    body: "Elite proficiency in Xplan, Xtools+ and Adviser Logic. No learning curve on your stack.",
  },
  {
    icon: Sparkles,
    title: "Complex Structures",
    body: "Deep specialisation in SMSFs, LRBAs, Family Trusts, and tax effective modeling.",
  },
];

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white border-b-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Portrait block */}
        <div className="lg:col-span-5 relative lg:sticky lg:top-28">
          <div className="absolute -top-3 -left-3 w-full h-full bg-[#FACC15]" aria-hidden />
          <div className="relative bg-white border-2 border-black p-6 shadow-brutal">
            <div className="aspect-[4/5] w-full bg-black overflow-hidden border-2 border-black">
              <img
                src="https://customer-assets.emergentagent.com/job_soa-automation/artifacts/hzjhdstm_IMG_1107.jpg"
                alt="Shanur Rahman, Founder of Banana Paraplanner"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="founder-portrait"
              />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="font-display font-black text-xl leading-none">Shanur Rahman</p>
                <p className="text-xs font-bold uppercase tracking-widest mt-1 text-black/70">Founder &amp; Senior Paraplanner</p>
              </div>
              <span className="inline-flex items-center gap-1 bg-[#FACC15] border-2 border-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest shadow-brutal">
                <Sparkles className="w-3 h-3" /> CFA L2
              </span>
            </div>
          </div>

          {/* Hobbies strip */}
          <div className="mt-6 bg-black text-white border-2 border-black p-5 shadow-brutal-yellow">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#FACC15]">Off the clock</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-center gap-2"><Dumbbell className="w-4 h-4 text-[#FACC15]" /> Lifting heavy weights</li>
              <li className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#FACC15]" /> Getting lost in a good book</li>
              <li className="flex items-center gap-2"><Car className="w-4 h-4 text-[#FACC15]" /> Drifting cars at the track</li>
            </ul>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-7">
          <span className="inline-block bg-[#FACC15] border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest shadow-brutal">
            Meet the Expert
          </span>
          <h2 className="font-display font-black mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            Half a decade of Australian paraplanning excellence.
          </h2>
          <p className="mt-4 text-base md:text-lg text-black/70 italic">
            Institutional grade expertise, delivered directly to your boutique practice.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {stats.map((s) => (
              <div
                key={s.label}
                data-testid={`about-stat-${s.label.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
                className="bg-white border-2 border-black p-4 shadow-brutal min-w-0"
              >
                <p className="font-display font-black text-2xl md:text-2xl lg:text-3xl text-black leading-none tracking-tighter whitespace-nowrap">{s.value}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest mt-2 text-black/70">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 space-y-6">
            {/* Pull quote */}
            <blockquote className="border-l-4 border-[#FACC15] pl-5 py-1">
              <p className="font-display font-bold text-xl md:text-2xl leading-tight tracking-tight">
                &ldquo;Every SOA is a piece of engineering. Not a typing exercise.&rdquo;
              </p>
              <footer className="mt-2 text-[10px] font-bold uppercase tracking-widest text-black/60">
                Shanur Rahman, Founder
              </footer>
            </blockquote>

            {/* Symmetric two column bio */}
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              <div className="bg-white border-2 border-black p-5 md:p-6 shadow-brutal">
                <p className="text-[10px] font-bold uppercase tracking-widest text-black/60">Background</p>
                <p className="mt-3 text-sm md:text-base text-black/85 leading-relaxed">
                  Half a decade in the engine room of large outsourcing agencies, delivering
                  1,100+ complex advice documents for top tier Australian practices managing
                  $200M+ in FUM each.
                </p>
                <p className="mt-3 text-sm md:text-base text-black/85 leading-relaxed">
                  The traditional BPO model is a slow, disconnected assembly line. Practice
                  managers deserve a direct technical partner, not a support ticket. That is why
                  I launched Banana Paraplanners.
                </p>
              </div>

              <div className="bg-white border-2 border-black p-5 md:p-6 shadow-brutal">
                <p className="text-[10px] font-bold uppercase tracking-widest text-black/60">Approach</p>
                <p className="mt-3 text-sm md:text-base text-black/85 leading-relaxed">
                  A CFA Level 2 Candidate with a B-Tech in Computer Science. I do not just type
                  up documents. I engineer them, blending deep financial theory with multiple
                  AI models running in parallel for cross checked accuracy.
                </p>
                <p className="mt-3 text-sm md:text-base text-black/85 leading-relaxed">
                  Advanced data modeling and intelligent automation future proof your back
                  office. SMSFs, LRBAs, family trusts. All handled with absolute focus on
                  precision, compliance and speed.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {techPoints.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-white border-2 border-black p-5 shadow-brutal flex items-start gap-3">
                  <Icon className="w-6 h-6 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-bold text-base">{p.title}</p>
                    <p className="text-xs text-black/70 mt-1 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
